import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { OwnerOnlyBanner } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Loader2, Clock, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [company, setCompany] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null); // "planId:interval"
  const [portalLoading, setPortalLoading] = useState(false);
  const [acceptedBillingTos, setAcceptedBillingTos] = useState(false);
  const [tosHighlight, setTosHighlight] = useState(false);
  const tosRef = useRef(null);

  const requireTos = () => {
    if (acceptedBillingTos) return true;
    tosRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTosHighlight(true);
    setTimeout(() => setTosHighlight(false), 2500);
    toast.error("Please review and accept the Terms and Refund Policy below to continue.");
    return false;
  };

  useEffect(() => {
    // Reconcile with Stripe first (reflects portal cancel/resume/plan changes
    // even if the webhook never reached us), then load the company record.
    api.get("/billing/sync").catch(() => {}).finally(() => {
      api.get("/companies/me").then((r) => setCompany(r.data));
    });
    api.get("/plans").then((r) => setPlans(r.data));
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPortalLoading(false);
    }
  };

  const upgrade = async (planId, interval = "weekly") => {
    if (!requireTos()) return;
    const key = `${planId}:${interval}`;
    setLoading(key);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        interval,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(null);
    }
  };

  const startTrial = async (interval = "weekly") => {
    if (!requireTos()) return;
    setLoading(`trial:${interval}`);
    try {
      const { data } = await api.post("/billing/start-trial", { interval, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(null);
    }
  };

  const currentPlan = company?.plan;
  const trialPending = company?.subscription_status === "trial_pending";
  const trialDaysLeft = company?.subscription_status === "trial" && company?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(company.trial_ends_at) - Date.now()) / 86400000))
    : null;

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const isTrialing = company?.subscription_status === "trial";
  const willCancel = !!company?.cancel_at_period_end;
  const endDate = fmtDate(company?.cancel_at || company?.trial_ends_at);
  const renewDate = fmtDate(company?.trial_ends_at);
  const premiumPlan = plans.find((p) => p.id === (company?.plan || "premium")) || plans[0];
  const renewAmount = company?.billing_interval === "yearly"
    ? (premiumPlan?.price_yearly ? `$${premiumPlan.price_yearly}/yr` : "the annual rate")
    : (premiumPlan?.price ? `$${premiumPlan.price}/wk` : "$74.99/wk");

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="billing-page">
      <OwnerOnlyBanner note="Only you can change your subscription — employees don't see this page." />

      {company?.billing_locked_reason && (
        <div className="border border-[#E13C32]/60 bg-[#E13C32]/10 p-4 rounded-sm mb-6 flex items-center gap-3" data-testid="billing-lock-alert">
          <AlertTriangle className="w-5 h-5 text-[#E13C32] flex-shrink-0" />
          <span className="text-sm">{company.billing_locked_reason}</span>
        </div>
      )}

      {company && (
        <div className="border border-[#27272A] bg-[#121212] p-6 rounded-sm mb-8 flex items-center justify-between gap-6 flex-wrap" data-testid="current-plan-card">
          <div>
            <div className="font-label text-[#A1A1AA] mb-2">CURRENT PLAN</div>
            <div className="font-display text-3xl">{company.plan_label?.toUpperCase()}</div>
            {company.billing_interval && (
              <div className="text-sm text-[#A1A1AA] mt-1">Billed {company.billing_interval}</div>
            )}
            {trialDaysLeft !== null && (
              <div className="flex items-center gap-2 mt-2 text-sm text-[#FFC832]" data-testid="trial-days-left">
                <Clock className="w-4 h-4" />
                <span><b>{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"}</b> left in your free trial.</span>
              </div>
            )}
            {!trialPending && (
              willCancel ? (
                <div className="flex items-center gap-2 mt-2 text-sm text-[#E8921F]" data-testid="renewal-indicator">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Set to <b>cancel</b>{isTrialing
                      ? <> — your plan will <b>not</b> renew{endDate ? ` when your trial ends on ${endDate}` : " after the trial"}.</>
                      : <> — access ends{endDate ? ` ${endDate}` : " at period end"}; won't renew.</>}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2 text-sm text-[#34C759]" data-testid="renewal-indicator">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Set to <b>renew</b>{isTrialing
                      ? <> automatically{renewDate ? ` when your trial ends on ${renewDate}` : " after the trial"} — then {renewAmount}.</>
                      : <> automatically{company.billing_interval ? ` (billed ${company.billing_interval})` : ""}.</>}
                  </span>
                </div>
              )
            )}
          </div>
          <div data-testid="plan-status-block">
            <div className="font-label text-[#A1A1AA] mb-1 text-[10px] tracking-widest">STATUS</div>
            <div className={`font-display text-2xl ${company.subscription_status === "active" ? "text-[#34C759]" : "text-white"}`}>
              {company.subscription_status?.toUpperCase()}
            </div>
            {company.cancel_at_period_end && (
              <div className="text-xs text-[#E8921F] mt-1" data-testid="cancel-scheduled-note">
                {company.cancel_at
                  ? `Cancels ${new Date(company.cancel_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                  : "Cancels at period end"}
              </div>
            )}
          </div>
          <div className="text-right" data-testid="plan-seats-block">
            <div className="font-label text-[#A1A1AA] mb-1 text-[10px] tracking-widest">SEATS USED</div>
            <div className="font-display text-2xl">{company.current_users}<span className="text-[#A1A1AA]"> / {company.max_users ?? "∞"}</span></div>
          </div>
        </div>
      )}

      {["trial", "active", "past_due"].includes(company?.subscription_status) && (
        <div className="mb-8 -mt-4" data-testid="manage-billing-block">
          <Button
            variant="outline"
            disabled={portalLoading}
            onClick={openPortal}
            className="font-label h-11 rounded-sm border-[#27272A] bg-transparent hover:bg-[#1A1A1A] text-white px-5 disabled:opacity-50"
            data-testid="manage-billing-button"
          >
            {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Manage billing & cancel"}
          </Button>
          <p className="text-xs text-[#A1A1AA] mt-2 max-w-xl">
            Opens your secure Stripe portal to update your card, view invoices, or cancel anytime — no email needed.
            {company?.subscription_status === "trial" ? " Cancel before your trial ends and you won't be charged." : ""}
          </p>
        </div>
      )}
        <div className="border border-[#34C759]/50 bg-[#34C759]/10 p-6 rounded-sm mb-8" data-testid="start-trial-card">
          <div className="font-display text-2xl mb-1">START YOUR 30-DAY FREE TRIAL</div>
          <p className="text-sm text-[#A1A1AA] mb-4 max-w-2xl">
            You won't be charged until the trial ends,
            and you can cancel anytime. After the trial it's $74.99/wk (or choose annual to save). Accept the Terms below to continue.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              disabled={loading === "trial:weekly"}
              onClick={() => startTrial("weekly")}
              className="font-label h-11 rounded-sm bg-[#34C759] hover:bg-[#2DB14E] text-black px-6 disabled:opacity-50"
              data-testid="start-trial-weekly-button"
            >
              {loading === "trial:weekly" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start free trial — then $74.99/wk"}
            </Button>
            <Button
              disabled={loading === "trial:yearly"}
              onClick={() => startTrial("yearly")}
              className="font-label h-11 rounded-sm bg-[#FFC832] hover:bg-[#FFD968] text-black px-6 disabled:opacity-50"
              data-testid="start-trial-yearly-button"
            >
              {loading === "trial:yearly" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start trial — then annual (2 months free)"}
            </Button>
          </div>
        </div>

      <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
        {plans.map((p) => {
          const isCurrent = currentPlan === p.id;
          return (
            <div
              key={p.id}
              className="relative border border-[#FFC832]/70 bg-[#121212] p-6 rounded-sm flex flex-col"
              style={{ boxShadow: "0 22px 42px -14px rgba(255,200,50,0.55), 0 6px 22px -6px rgba(255,200,50,0.22)" }}
              data-testid={`billing-plan-${p.id}`}
            >
              <div className="font-display text-2xl">{p.label.toUpperCase()}</div>
              <div className="text-xs font-label text-[#A1A1AA] mt-1 mb-3 tracking-wider">{p.seat_range?.toUpperCase()}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-4xl">${p.price}</span>
                <span className="text-sm text-[#A1A1AA]">/wk</span>
              </div>
              {p.price_yearly ? (
                <div className="text-xs text-[#FFC832] mt-1 mb-4" data-testid="premium-annual-note">
                  annual billing available — {p.yearly_note}
                </div>
              ) : (
                <div className="mb-4" />
              )}
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start gap-2 font-medium text-white">
                  <Check className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
                  <span>{p.max_users != null ? `Up to ${p.max_users} sales reps` : "Unlimited sales reps"}</span>
                </li>
                {p.features.map((f, i) => {
                  const isBeta = f.startsWith("Early access");
                  return (
                    <li key={i} className="flex items-start gap-2">
                      {isBeta
                        ? <Sparkles className="w-4 h-4 text-[#FFC832] mt-0.5 flex-shrink-0" />
                        : <Check className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />}
                      <span className={isBeta ? "text-[#FFC832]" : ""}>{f}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-auto space-y-2">
                <Button
                  disabled={isCurrent || loading === `${p.id}:weekly`}
                  onClick={() => upgrade(p.id, "weekly")}
                  className={`w-full font-label h-11 rounded-sm disabled:opacity-50 ${
                    isCurrent ? "bg-[#27272A] hover:bg-[#27272A] text-[#A1A1AA]" : "bg-[#178AE5] hover:bg-[#41A0E8] text-white"
                  }`}
                  data-testid={`upgrade-${p.id}-button`}
                >
                  {loading === `${p.id}:weekly` ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    isCurrent ? "Current plan" : `Subscribe weekly — $${p.price}/wk`}
                </Button>
                {p.price_yearly && (
                  <Button
                    disabled={loading === `${p.id}:yearly`}
                    onClick={() => upgrade(p.id, "yearly")}
                    className="w-full font-label h-11 rounded-sm disabled:opacity-50 bg-[#FFC832] hover:bg-[#FFD968] text-black"
                    data-testid={`upgrade-${p.id}-annual-button`}
                  >
                    {loading === `${p.id}:yearly` ? <Loader2 className="w-4 h-4 animate-spin" /> : "Go Annual — 2 months free"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div ref={tosRef} className={`mt-6 p-4 border bg-[#121212] rounded-sm flex items-start gap-3 transition-all duration-300 ${tosHighlight ? "border-[#E13C32] ring-2 ring-[#E13C32]/60 shadow-[0_0_24px_-4px_rgba(225,60,50,0.55)]" : "border-[#27272A]"}`} data-testid="billing-tos-container">
        <Checkbox
          id="billing-tos"
          checked={acceptedBillingTos}
          onCheckedChange={(v) => setAcceptedBillingTos(v === true)}
          className="mt-0.5 border-[#27272A] data-[state=checked]:bg-[#E13C32] data-[state=checked]:border-[#E13C32]"
          data-testid="billing-tos-checkbox"
        />
        <label htmlFor="billing-tos" className="text-sm text-[#A1A1AA] leading-relaxed cursor-pointer select-none">
          I agree to the{" "}
          <Link to="/terms" target="_blank" className="text-[#178AE5] hover:underline" onClick={(e) => e.stopPropagation()}>Terms of Service</Link>
          {", "}
          <Link to="/refund-policy" target="_blank" className="text-[#178AE5] hover:underline" onClick={(e) => e.stopPropagation()}>Refund Policy</Link>
          {", and authorise recurring billing (weekly, or annual if selected) until I cancel. I understand my plan will change immediately upon successful payment."}
        </label>
      </div>

      <p className="text-xs text-[#A1A1AA] mt-6">
        🔒 Payments processed securely by Stripe.
      </p>
    </div>
  );
}
